import Vue, { CreateElement } from 'vue';
import VueRouter from 'vue-router';
import Vuex from 'vuex';
import VueI18n from 'vue-i18n';
import UiKit from '@uikit/ui-kit';
import { TSyncProp } from '@ui/frontend-app-manager';
import { Entry } from './types/Entry';
import { AnalyticsPlugin } from './plugins/AnalyticsPlugin';
import { Router, createRouter } from './router';
import { Store, StateSyncPlugin, createStore, createStateSyncPlugin } from './store';
import { I18n, createI18n } from './i18n';
import FrontendAppComponent from './FrontendAppComponent.vue';
import {
  SYNC_PROP_TENANT_UUID,
  SYNC_PROP_SELF_TENANT_UUID,
  SYNC_PROP_LANGUAGE,
  SYNC_PROP_BRANDING
} from './constants';

export type BootstrapProps = any;
export type MountProps = {
  container: HTMLElement;
  syncProps?: SyncProps;
  [key: string]: any;
};
export type UnmountProps = any;

export type LifecycleHooks = {
  mount: (props: MountProps) => Promise<void>;
  bootstrap?: (props?: any) => Promise<void>;
  unmount?: (props?: any) => Promise<void>;
};

export type SyncProps = { [propId: string]: TSyncProp };

export type FrontendAppOptions = {
  entry?: Entry,
  analytics?: boolean
};

export class FrontendApp {
  private entry: Entry = Entry.Wizard;

  public instance: Vue | null = null;

  public container: HTMLElement | null = null;

  constructor(options?: FrontendAppOptions) {
    Vue.use(VueRouter);
    Vue.use(Vuex);
    Vue.use(VueI18n);
    Vue.use(UiKit);

    if (options?.entry) {
      this.entry = options.entry;
    }
    if (options?.analytics) {
      Vue.use(AnalyticsPlugin);
    }
  }

  public async bootstrap(props: BootstrapProps): Promise<void> {
    return Promise.resolve();
  }

  public async mount(props: MountProps): Promise<void> {
    const router: Router = createRouter();
    const stateSyncPlugin: StateSyncPlugin = createStateSyncPlugin({
      defaultState: props?.syncProps || {
        [SYNC_PROP_SELF_TENANT_UUID]: '',
        [SYNC_PROP_TENANT_UUID]: '',
        [SYNC_PROP_LANGUAGE]: '',
        [SYNC_PROP_BRANDING]: null
      }
    });
    const store: Store = createStore({
      plugins: [
        stateSyncPlugin
      ]
    });
    const i18n: I18n = createI18n();

    this.instance = new Vue({
      router,
      store,
      i18n,
      render: (createElement: CreateElement) => createElement(FrontendAppComponent, {
        props: {
          entry: this.entry
        }
      })
    });
    this.instance.$mount(document.createElement('div'));

    this.container = props.container;
    this.container.appendChild(this.instance.$el);
  }

  public async unmount(props?: UnmountProps): Promise<void> {
    if (this.container && this.instance) {
      this.container.removeChild(this.instance.$el);
    }
  }

  public getLifecycleHooks(): LifecycleHooks {
    return {
      bootstrap: this.bootstrap.bind(this),
      mount: this.mount.bind(this),
      unmount: this.unmount.bind(this)
    };
  }
}

export function createFrontendApp(options?: FrontendAppOptions): FrontendApp {
  return new FrontendApp(options);
}
